import { HttpClient, HttpHeaders, HttpParams, httpResource, HttpResourceRef } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { REQUIRE_AUTH_HEADER } from '@psu-pennstateit-swe/utils/security';
import { map, Observable, of } from 'rxjs';
import { SSRM_CONFIG } from '../../app-properties.model';
import { Relationship, relationshipSchema, ServerRelationship, serverRelationshipSchema } from './relationships.model';

const headers = new HttpHeaders({ [REQUIRE_AUTH_HEADER]: 'true' });
const relationshipLookupSchema = serverRelationshipSchema.pick({ displayName: true, externalId: true }).array();

@Injectable({
  providedIn: 'root',
})
export class RelationshipsData {
  private readonly http = inject(HttpClient);
  private readonly ssrmConfig = inject(SSRM_CONFIG);
  private readonly baseUrl = `${this.ssrmConfig.url}relationships`;

  /** Last successfully loaded relationships. */
  private readonly relationshipsData = signal<Relationship[] | null>(null);
  readonly relationshipsState = this.relationshipsData.asReadonly();

  relationshipsResource(): HttpResourceRef<Relationship[] | undefined> {
    return httpResource<Relationship[]>(
      () => ({
        url: this.baseUrl,
        method: 'GET',
        headers,
      }),
      {
        parse: (response): Relationship[] => {
          const relationships = relationshipSchema.array().parse(response);

          this.relationshipsData.set(relationships);
          return relationships;
        },
      },
    );
  }

  /** Checks for matching relationships with a single request, without changing the cached list. */
  checkRelationshipExists(query: { displayName?: string; externalId?: string }): Observable<boolean> {
    let params = new HttpParams();
    if (query.displayName !== undefined) {
      params = params.set('displayName', query.displayName);
    }
    if (query.externalId !== undefined) {
      params = params.set('externalId', query.externalId);
    }
    if (!params.keys().length) {
      return of(false);
    }

    return this.http
      .get<ServerRelationship[]>(this.baseUrl, { headers, params })
      .pipe(
        map((response) =>
          relationshipLookupSchema
            .parse(response)
            .some(
              (relationship) =>
                (query.displayName === undefined || relationship.displayName === query.displayName) &&
                (query.externalId === undefined || relationship.externalId === query.externalId),
            ),
        ),
      );
  }

  /**
   * Creates an HTTP resource for relationships.
   */
  getRelationships(): HttpResourceRef<Relationship[] | undefined> {
    return this.relationshipsResource();
  }
}
